/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx, css, cssMap, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import type { CustomTriggerProps } from '@atlaskit/dropdown-menu/types';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import HistoryIcon from '@atlaskit/icon-lab/core/history';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import CrossIcon from '@atlaskit/icon/core/cross';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import Lozenge from '@atlaskit/lozenge/new/lozenge';
import { Flex, Inline, Pressable, Text, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import type { Suggestion } from '../../../aiSuggestionsPluginType';
import { SuggestionAgentIdentity } from './SuggestionAgentIdentity';
import { SuggestionCardSources } from './SuggestionCardSources';
import { SuggestionSourceAppStack } from './SuggestionSourceAppStack';

export const SUGGESTION_CARD_TESTID = 'ai-suggestion-card';

const tooltipAbove = { position: 'top' } as const;

type SuggestionCardProps = {
	/**
	 * Optional banner (e.g. `<StatusBanner status="stale" />`) shown above the
	 * short description. `SuggestionCard` doesn't know what a banner is or
	 * when to show one — the caller decides and passes the already-built
	 * element.
	 */
	banner?: React.ReactNode;
	isHidingDeletedDiffs?: boolean;
	/**
	 * The selected suggestion is being regenerated (stale check → regenerate).
	 * While true, the action buttons are disabled so the user can't act on
	 * about-to-be-replaced content.
	 */
	isRegenerating?: boolean;
	onAccept?: () => void;
	onBadSuggestion?: () => void;
	onClose?: () => void;
	onDiscard?: () => void;
	onDismiss?: () => void;
	onGoodSuggestion?: () => void;
	onNavigate?: (direction: 'up' | 'down') => void;
	onReasoningViewed?: () => void;
	onShowSidebar?: () => void;
	onToggleDeletedDiffs?: () => void;
	showComparisonButton?: boolean;
	suggestion: Suggestion;
};

// Shimmer applied to the description while the suggestion is regenerating.
// Copied from rovo-platform-ui-components' ShimmerText so we don't couple to it.
const shimmerAnimation = keyframes({
	'0%': {
		backgroundPosition: '200% 0',
	},
	'100%': {
		backgroundPosition: '-200% 0',
	},
});

const shimmerGradient = css({
	background: `linear-gradient(
		90deg,
		${token('color.text.subtlest')} 0%,
		${token('color.text.subtlest')} 20%,
		#c7cddc 40%,
		${token('color.text.subtlest')} 60%,
		${token('color.text.subtlest')} 80%,
		#c7cddc 90%,
		${token('color.text.subtlest')} 100%
	)`,
	backgroundSize: '200% 100%',
	WebkitBackgroundClip: 'text',
	WebkitTextFillColor: 'transparent',
	backgroundClip: 'text',
	animation: `${shimmerAnimation} 4s linear infinite`,
});

const styles = cssMap({
	sourceButton: {
		display: 'flex',
		backgroundColor: 'transparent',
		alignItems: 'center',
		paddingRight: token('space.0'),
		paddingLeft: token('space.0'),
		gap: token('space.075'),
		color: token('color.text.subtle'),
		'&:disabled': {
			color: token('color.text.disabled'),
		},
	},
	detailsContainer: {
		width: '100%',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		// First-listed layer paints on top: scrim, then grid, then the base colour.
		backgroundImage: `radial-gradient(
			circle at top left,
			${token('elevation.surface.sunken')} -120%,
			transparent 120%
		), linear-gradient(
			${token('color.background.neutral')} ${token('border.width')},
			transparent ${token('border.width')}
		), linear-gradient(
			90deg,
			${token('color.background.neutral')} ${token('border.width')},
			transparent ${token('border.width')}
		)`,
		backgroundSize: `100% 100%, ${token('space.400')} ${token('space.400')}, ${token(
			'space.400',
		)} ${token('space.400')}`,
		// Offset by one line width so no grid line sits on the container's edge.
		backgroundPosition: `0 0, calc(-1 * ${token('border.width')}) calc(-1 * ${token(
			'border.width',
		)}), calc(-1 * ${token('border.width')}) calc(-1 * ${token('border.width')})`,
		borderRadius: token('radius.large'),
		paddingBlock: token('space.150'),
	},
	reasoningContent: {
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
	},
	card: {
		position: 'relative',
		backgroundColor: token('elevation.rovo.surface.overlay'),
		borderColor: token('color.border.inverse'),
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderWidth: '0.25px',
		borderRadius: token('radius.xlarge'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		boxShadow: token('elevation.shadow.overlay'),
		// Kept in sync with `CARD_WIDTH_FALLBACK` in `useSuggestionCardPositioning.ts`,
		// which uses it to decide whether the card fits to the right of its target
		// before the card has been measured.
		width: '280px',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		boxSizing: 'border-box',
	},
	privateMetadata: {
		flexShrink: 0,
		// Block-level flex, not the default block: Tooltip wraps its child in a `div`, and an
		// inline-level child there would sit in a line box whose inherited `line-height` adds
		// leading above the 20px lozenge, making it taller than the app stack beside it.
		display: 'flex',
		alignItems: 'center',
	},
	restrictedMetadata: {
		alignItems: 'center',
		cursor: 'default',
		// `flex` rather than `inline-flex`, and no `vertical-align`, so this box is not
		// baseline-aligned inside a line box. See `privateMetadata`.
		display: 'flex',
		overflow: 'hidden',
	},
	restrictedContent: {
		alignItems: 'center',
		columnGap: token('space.0'),
		display: 'inline-grid',
		gridTemplateColumns: 'auto minmax(0, 0fr)',
		marginInlineEnd: `calc(-1 * ${token('border.width')})`,
		marginInlineStart: `calc(-1 * ${token('border.width')})`,
		transitionDuration: token('motion.duration.medium'),
		transitionProperty: 'grid-template-columns, column-gap, margin-inline-start, margin-inline-end',
		transitionTimingFunction: token('motion.easing.inout.bold'),
		verticalAlign: 'middle',
	},
	restrictedContentExpanded: {
		columnGap: token('space.050'),
		gridTemplateColumns: 'auto minmax(0, 1fr)',
		marginInlineEnd: token('space.0'),
		marginInlineStart: token('space.0'),
	},
	restrictedLabel: {
		minWidth: token('space.0'),
		opacity: 0,
		overflow: 'hidden',
		transitionDuration: token('motion.duration.medium'),
		transitionProperty: 'opacity',
		transitionTimingFunction: token('motion.easing.out.practical'),
		whiteSpace: 'nowrap',
	},
	restrictedLabelExpanded: {
		opacity: 1,
	},
	reasoningHeader: {
		width: '100%',
	},
});

export const SuggestionCard: React.ForwardRefExoticComponent<
	SuggestionCardProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, SuggestionCardProps>(function SuggestionCard(
	{
		banner,
		onAccept,
		onDiscard,
		onDismiss,
		onGoodSuggestion,
		onBadSuggestion,
		onNavigate,
		onReasoningViewed,
		onClose,
		onShowSidebar,
		onToggleDeletedDiffs,
		isHidingDeletedDiffs,
		isRegenerating = false,
		showComparisonButton = false,
		suggestion,
	},
	ref,
): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isSourcesExpanded, setIsSourcesExpanded] = React.useState(false);
	const [isRestrictedHovered, setIsRestrictedHovered] = React.useState(false);

	const handleSourcesToggle = useCallback(() => {
		if (!isSourcesExpanded) {
			onReasoningViewed?.();
		}
		setIsSourcesExpanded((previous) => !previous);
	}, [isSourcesExpanded, onReasoningViewed]);

	const renderMoreOptionsTrigger = useCallback(
		({
			triggerRef,
			'aria-controls': ariaControls,
			'aria-expanded': ariaExpanded,
			'aria-haspopup': ariaHasPopup,
			onClick,
			isSelected,
		}: CustomTriggerProps<HTMLButtonElement>) => (
			<IconButton
				ref={triggerRef}
				aria-controls={ariaControls}
				aria-expanded={ariaExpanded}
				aria-haspopup={ariaHasPopup}
				isDisabled={isRegenerating}
				isSelected={isSelected}
				onClick={onClick}
				icon={ShowMoreHorizontalIcon}
				label={formatMessage(aiSuggestionsMessages.cardMoreOptionsButtonLabel)}
				isTooltipDisabled={false}
				tooltip={tooltipAbove}
				appearance="subtle"
				spacing="default"
			/>
		),
		[formatMessage, isRegenerating],
	);

	// `IconButton` already renders its `label` as both the accessible name and the
	// tooltip, so these icons are decorative.
	const ChevronUpSmall = useCallback(() => {
		return <ChevronUpIcon label="" size="small" />;
	}, []);

	const ChevronDownSmall = useCallback(() => {
		return <ChevronDownIcon label="" size="small" />;
	}, []);

	const isInsight = suggestion.actionKind === 'INFORMATIONAL';
	// An out-of-date suggestion can no longer be safely applied — only discard.
	const isStale = suggestion.status === 'STALE';
	// The restricted lozenge warns that the suggestion's sources aren't visible to
	// every editor, so it's only meaningful when the suggestion has sources.
	const hasSources = !!suggestion.sources?.length;

	return (
		<div
			ref={ref}
			css={[styles.card]}
			data-testid={SUGGESTION_CARD_TESTID}
			data-suggestion-id={suggestion.id}
		>
			<Flex alignItems="center" justifyContent="space-between">
				<Inline space="space.0" alignBlock="center">
					<IconButton
						icon={ChevronUpSmall}
						label={formatMessage(aiSuggestionsMessages.cardPreviousSuggestionButtonLabel)}
						isTooltipDisabled={false}
						tooltip={tooltipAbove}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('up') : undefined}
					/>
					<IconButton
						icon={ChevronDownSmall}
						label={formatMessage(aiSuggestionsMessages.cardNextSuggestionButtonLabel)}
						isTooltipDisabled={false}
						tooltip={tooltipAbove}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('down') : undefined}
					/>
				</Inline>
				<Inline space="space.050" alignBlock="center">
					{onShowSidebar && (
						<IconButton
							icon={PanelRightIcon}
							label={formatMessage(aiSuggestionsMessages.cardShowSidebarButtonLabel)}
							isTooltipDisabled={false}
							tooltip={tooltipAbove}
							appearance="subtle"
							spacing="compact"
							onClick={onShowSidebar}
						/>
					)}
					<IconButton
						icon={CrossIcon}
						label={formatMessage(aiSuggestionsMessages.cardCloseButtonLabel)}
						isTooltipDisabled={false}
						tooltip={tooltipAbove}
						appearance="subtle"
						spacing="compact"
						onClick={onClose}
					/>
				</Inline>
			</Flex>

			{banner}
			<Stack space="space.100">
				{isRegenerating ? (
					<div css={shimmerGradient}>
						<Text
							as="p"
							size="medium"
							weight="medium"
							color={isRegenerating ? 'color.text.disabled' : 'color.text.subtlest'}
						>
							{suggestion.description}
						</Text>
					</div>
				) : (
					<Text as="p" size="medium" weight="medium" color="color.text">
						{suggestion.description}
					</Text>
				)}
				{suggestion.reasoning && (
					<Flex alignItems="start" direction="column" gap="space.100">
						<Flex alignItems="center" justifyContent="space-between" xcss={styles.reasoningHeader}>
							<Pressable
								xcss={styles.sourceButton}
								onClick={handleSourcesToggle}
								isDisabled={isRegenerating}
								aria-expanded={isSourcesExpanded}
							>
								<Text size="medium" weight="medium" color="inherit">
									{formatMessage(aiSuggestionsMessages.cardDetailsButtonLabel)}
								</Text>
								{isSourcesExpanded ? (
									<ChevronDownIcon label="" size="small" />
								) : (
									<ChevronRightIcon label="" size="small" />
								)}
							</Pressable>
							{hasSources && (
								<Inline space="space.050" alignBlock="center">
									<div css={styles.privateMetadata}>
										<Tooltip
											position="top"
											content={formatMessage(aiSuggestionsMessages.cardRestrictedTooltipLabel)}
											ignoreTooltipPointerEvents
										>
											<span
												css={styles.restrictedMetadata}
												data-testid="ai-suggestion-card-restricted"
												onMouseEnter={() => setIsRestrictedHovered(true)}
												onMouseLeave={() => setIsRestrictedHovered(false)}
												onFocus={() => setIsRestrictedHovered(true)}
												onBlur={() => setIsRestrictedHovered(false)}
											>
												<Lozenge appearance="neutral">
													<span
														css={[
															styles.restrictedContent,
															isRestrictedHovered && styles.restrictedContentExpanded,
														]}
													>
														<LockLockedIcon
															label=""
															size="small"
															color={token('color.icon.subtle')}
														/>
														<span
															css={[
																styles.restrictedLabel,
																isRestrictedHovered && styles.restrictedLabelExpanded,
															]}
														>
															<Text as="span" color="inherit" size="small">
																{formatMessage(aiSuggestionsMessages.cardRestrictedLabel)}
															</Text>
														</span>
													</span>
												</Lozenge>
											</span>
										</Tooltip>
									</div>
									<SuggestionSourceAppStack sources={suggestion.sources} />
								</Inline>
							)}
						</Flex>
						{isSourcesExpanded && (
							<Stack
								xcss={styles.detailsContainer}
								space="space.250"
								testId="ai-suggestion-card-details"
							>
								<div css={styles.reasoningContent}>
									<Text as="p" color="color.text">
										{suggestion.reasoning}
									</Text>
								</div>
								{!!suggestion?.sources?.length && (
									<SuggestionCardSources sources={suggestion.sources} />
								)}
							</Stack>
						)}
					</Flex>
				)}
			</Stack>
			<Flex alignItems="center" justifyContent="space-between" gap="space.100">
				<SuggestionAgentIdentity
					agent={suggestion.createdByAgent}
					user={suggestion.createdByUser}
				/>
				<Inline space="space.050" alignBlock="center">
					{!isInsight &&
						(onToggleDeletedDiffs || onGoodSuggestion || onBadSuggestion) &&
						(showComparisonButton && onToggleDeletedDiffs ? (
							<Inline space="space.050" alignBlock="center">
								<IconButton
									icon={isHidingDeletedDiffs ? EyeOpenStrikethroughIcon : EyeOpenIcon}
									label={formatMessage(
										isHidingDeletedDiffs
											? aiSuggestionsMessages.cardShowDeletedDiffsLabel
											: aiSuggestionsMessages.cardHideDeletedDiffsLabel,
									)}
									appearance="subtle"
									spacing="compact"
									isDisabled={isRegenerating}
									onClick={onToggleDeletedDiffs}
								/>
								{(onGoodSuggestion || onBadSuggestion) && (
									<DropdownMenu<HTMLButtonElement>
										placement="bottom-end"
										trigger={renderMoreOptionsTrigger}
									>
										<DropdownItemGroup>
											{onGoodSuggestion && (
												<DropdownItem
													elemBefore={<ThumbsUpIcon label="" />}
													onClick={onGoodSuggestion}
												>
													{formatMessage(aiSuggestionsMessages.cardGoodSuggestionLabel)}
												</DropdownItem>
											)}
											{onBadSuggestion && (
												<DropdownItem
													elemBefore={<ThumbsDownIcon label="" />}
													onClick={onBadSuggestion}
												>
													{formatMessage(aiSuggestionsMessages.cardBadSuggestionLabel)}
												</DropdownItem>
											)}
										</DropdownItemGroup>
									</DropdownMenu>
								)}
							</Inline>
						) : (
							<DropdownMenu<HTMLButtonElement>
								placement="bottom-end"
								trigger={renderMoreOptionsTrigger}
							>
								<DropdownItemGroup>
									{onToggleDeletedDiffs && (
										<DropdownItem
											// The same icon in both states: the item's label already says which
											// way the toggle goes, so a changing icon just adds noise.
											elemBefore={<HistoryIcon label="" />}
											onClick={onToggleDeletedDiffs}
										>
											{isHidingDeletedDiffs
												? formatMessage(aiSuggestionsMessages.cardShowDeletedDiffsLabel)
												: formatMessage(aiSuggestionsMessages.cardHideDeletedDiffsLabel)}
										</DropdownItem>
									)}
									{onGoodSuggestion && (
										<DropdownItem elemBefore={<ThumbsUpIcon label="" />} onClick={onGoodSuggestion}>
											{formatMessage(aiSuggestionsMessages.cardGoodSuggestionLabel)}
										</DropdownItem>
									)}
									{onBadSuggestion && (
										<DropdownItem
											elemBefore={<ThumbsDownIcon label="" />}
											onClick={onBadSuggestion}
										>
											{formatMessage(aiSuggestionsMessages.cardBadSuggestionLabel)}
										</DropdownItem>
									)}
								</DropdownItemGroup>
							</DropdownMenu>
						))}
					{isInsight ? (
						<Button onClick={onDismiss} isDisabled={isRegenerating}>
							{formatMessage(aiSuggestionsMessages.cardDismissButtonLabel)}
						</Button>
					) : (
						<React.Fragment>
							<Button
								appearance={isStale ? 'primary' : 'subtle'}
								onClick={onDiscard}
								isDisabled={isRegenerating}
							>
								{formatMessage(aiSuggestionsMessages.cardDiscardButtonLabel)}
							</Button>
							{/* A stale suggestion can only be discarded — hide Accept. */}
							{!isStale && (
								<Button appearance="primary" onClick={onAccept} isDisabled={isRegenerating}>
									{formatMessage(aiSuggestionsMessages.cardAcceptButtonLabel)}
								</Button>
							)}
						</React.Fragment>
					)}
				</Inline>
			</Flex>
		</div>
	);
});
